import { AttributeApiModel } from '../../../types/models/attribute.model';
import { NodeApiModel } from '../../../types/models/node.model';
import { GetNodeApiRequest } from '../../../types/requests/ontology/get-node.request';
import { DeleteNodeApiRequest } from '../../../types/requests/ontology/delete-node.request';
import { SearchOntologyApiRequest } from '../../../types/requests/ontology/search-ontology.request';
import { CursorPaginatedApiResponse } from '../../../types/responses/cursor-paginated.response';
import { client } from './client';
import { CreateNodeApiRequest } from '../../../types/requests/ontology/create-node.request';
import { UpdateNodeApiRequest } from '../../../types/requests/ontology/update-node.request';
import { SearchNodeAttributesApiRequest } from '../../../types/requests/ontology/attributes/search-node-attributes.request';
import { AddAttachmentToNodeApiRequest } from '../../../types/requests/ontology/attachments/add-attachment-to-node.request';
import { RemoveAttachmentFromNodeApiRequest } from '../../../types/requests/ontology/attachments/remove-attachment-from-node.request';
import { GetAllAttachmentsForNodeRequest } from '../../../types/requests/ontology/attachments/get-all-attachments-for-node.request';
import { DeleteNodeAttributeApiRequest } from '../../../types/requests/ontology/attributes/delete-node-attribute.request';
import { CreateNodeAttributeApiRequest } from '../../../types/requests/ontology/attributes/create-node-attribute.request';
import { UpdateNodeAttributeApiRequest } from '../../../types/requests/ontology/attributes/update-node-attribute.request';
import { GetNodeAttributeApiRequest } from '../../../types/requests/ontology/attributes/get-node-attribute.request';
import { AddDomainToNodeApiRequest } from '../../../types/requests/ontology/add-domain-to-node.request';
import { RemoveDomainFromNodeApiRequest } from '../../../types/requests/ontology/remove-domain-from-node.request';
import { AddDomainToAttributeApiRequest } from '../../../types/requests/ontology/attributes/add-domain-to-attribute.request';
import { RemoveDomainFromAttributeApiRequest } from '../../../types/requests/ontology/attributes/remove-domain-from-attribute.request';

export const getNode = <T extends NodeApiModel>({ nodeId, ...params }: GetNodeApiRequest) =>
  client.get<T>(`/ontology/${nodeId}`, { params });

export const searchOntology = (params: SearchOntologyApiRequest) =>
  client.get<CursorPaginatedApiResponse<NodeApiModel>>(`/ontology`, { params });

export const createNode = (params: CreateNodeApiRequest) => client.post<NodeApiModel>('ontology', params);

export const updateNode = ({ nodeId, nodeType, ...params }: UpdateNodeApiRequest) =>
  client.patch<NodeApiModel>(`/ontology/${nodeId}`, params, { params: { nodeType } });

export const deleteNode = ({ nodeId, nodeType }: DeleteNodeApiRequest) =>
  client.delete<void>(`/ontology/${nodeId}`, { params: { nodeType } });

export const createNodeAttribute = ({ nodeId, ...params }: CreateNodeAttributeApiRequest) =>
  client.post<AttributeApiModel>(`/ontology/${nodeId}/attributes`, params);

export const updateNodeAttribute = ({ nodeId, attributeId, ...params }: UpdateNodeAttributeApiRequest) =>
  client.patch<AttributeApiModel>(`/ontology/${nodeId}/attributes/${attributeId}`, params);

export const getNodeAttribute = <T extends AttributeApiModel>({
  nodeId,
  attributeId,
  ...params
}: GetNodeAttributeApiRequest) => client.get<T>(`/ontology/${nodeId}/attributes/${attributeId}`, { params });

export const searchNodeAttributes = <T extends AttributeApiModel>({
  nodeId,
  ...params
}: SearchNodeAttributesApiRequest) =>
  client.get<CursorPaginatedApiResponse<T>>(`/ontology/${nodeId}/attributes`, { params });

export const deleteNodeAttribute = ({ nodeId, attributeId, ...params }: DeleteNodeAttributeApiRequest) =>
  client.delete<void>(`/ontology/${nodeId}/attributes/${attributeId}`, { params });

export const addDomainToNode = ({ nodeId, domainId }: AddDomainToNodeApiRequest) =>
  client.post<void>(`/ontology/${nodeId}/domains/${domainId}`);

export const removeDomainFromNode = ({ nodeId, domainId }: RemoveDomainFromNodeApiRequest) =>
  client.delete<void>(`/ontology/${nodeId}/domains/${domainId}`);

export const addDomainToNodeAttribute = ({ nodeId, attributeId, domainId }: AddDomainToAttributeApiRequest) =>
  client.post<void>(`/ontology/${nodeId}/attributes/${attributeId}/domains/${domainId}`);

export const removeDomainFromNodeAttribute = ({ nodeId, attributeId, domainId }: RemoveDomainFromAttributeApiRequest) =>
  client.delete<void>(`/ontology/${nodeId}/attributes/${attributeId}/domains/${domainId}`);

export const addAttachmentToNode = ({ nodeId, nodeType, ...params }: AddAttachmentToNodeApiRequest) =>
  client.post<{ attachments: string[] }>(`/ontology/${nodeId}/attachments`, params, {
    params: { nodeType },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const getAllAttachmentsForNode = ({ nodeId, nodeType }: GetAllAttachmentsForNodeRequest) =>
  client.get<{ attachments: string[] }>(`/ontology/${nodeId}/attachments`, { params: { nodeType } });

export const removeAttachmentFromNode = ({ nodeId, nodeType, attachment }: RemoveAttachmentFromNodeApiRequest) =>
  client.delete<{ attachments: string[] }>(`/ontology/${nodeId}/attachments/${encodeURIComponent(attachment)}`, { params: { nodeType } });
